import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaChartBar, FaWhatsapp, FaCog, FaHome, FaClipboardList, FaUsers, FaCalendarAlt, FaComments, FaUserFriends, FaCrown, FaBuilding, FaSignOutAlt, FaUserCircle, FaMoneyBillWave, FaBell } from 'react-icons/fa';

const AdminSidebar = ({ activeView, onNavigate, onLogout, userInfo, logoSrc, isMobile = false, hideHeader = false, userType = 'Admin' }) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleSidebar = () => {
        setIsOpen(!isOpen);
    };

    const handleNavigation = (view) => {
        onNavigate(view);
        setIsOpen(false); // Close sidebar on mobile after navigation
    };

    // Listen for toggle event from other components
    useEffect(() => {
        const handleToggle = () => {
            setIsOpen(prev => !prev);
        };

        window.addEventListener('toggle-sidebar', handleToggle);

        return () => {
            window.removeEventListener('toggle-sidebar', handleToggle);
        };
    }, []); // No dependencies needed with callback

    // Different menu items based on user type and current view
    const isDashboardView = activeView.startsWith('dashboard-') || activeView === 'users' || activeView === 'agent-dashboard' || activeView === 'agent-credits' || activeView === 'agent-account';
    
    // Agent menu: Valuar, Valuaciones, Premium, and Mi cuenta
    const agentMenuItems = isDashboardView ? [
        { id: 'main', label: 'Valuar', icon: <FaHome /> },
        { type: 'divider' },
        { id: 'agent-dashboard', label: 'Valuaciones', icon: <FaClipboardList /> },
        { id: 'agent-credits', label: 'Premium', icon: <FaCrown /> },
        { id: 'agent-account', label: 'Mi cuenta', icon: <FaUserCircle /> },
    ] : [
        { id: 'agent-dashboard', label: 'Valuaciones', icon: <FaClipboardList /> },
        { id: 'agent-credits', label: 'Premium', icon: <FaCrown /> },
        { id: 'agent-account', label: 'Mi cuenta', icon: <FaUserCircle /> },
    ];
    
    // Admin menu: Full access
    const adminMenuItems = isDashboardView ? [
        { id: 'main', label: 'Tasador', icon: <FaHome /> },
        { type: 'divider' },
        { id: 'dashboard-metricas', label: 'Métricas', icon: <FaChartBar /> },
        { id: 'dashboard-finanzas', label: 'Finanzas', icon: <FaMoneyBillWave /> },
        { id: 'dashboard-valuaciones', label: 'Valuaciones', icon: <FaClipboardList /> },
        { id: 'dashboard-leads', label: 'Leads', icon: <FaUsers /> },
        { id: 'dashboard-seguimientos', label: 'Seguimientos', icon: <FaBell /> },
        { id: 'dashboard-citas', label: 'Citas', icon: <FaCalendarAlt /> },
        { id: 'dashboard-mensajes', label: 'Mensajes', icon: <FaComments /> },
        { id: 'dashboard-whatsapp', label: 'WhatsApp', icon: <FaWhatsapp /> },
        { id: 'users', label: 'Usuarios', icon: <FaUserFriends /> },
        { id: 'dashboard-ajustes', label: 'Ajustes', icon: <FaCog /> },
    ] : [
        { id: 'dashboard-metricas', label: 'Dashboard', icon: <FaChartBar /> },
    ];
    
    // Select menu based on user type
    const menuItems = (userType === 'agent' || userType === 'individual') ? agentMenuItems : adminMenuItems;

    return (
        <>
            {/* Header Bar - Sticky (hidden on mobile messages page) */}
            {!hideHeader && (
                <div style={{
                    width: '100%',
                    height: '80px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000
                }}>
                {/* Hamburger Button - In header */}
                <button
                    onClick={toggleSidebar}
                    style={{
                        position: 'absolute',
                        left: '20px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        fontSize: '20px'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                    }}
                >
                    {isOpen ? <FaTimes /> : <FaBars />}
                </button>

                {/* Logo in center */}
                {logoSrc && (
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <img 
                            src={logoSrc} 
                            alt="TasacionesYa Logo" 
                            style={{
                                height: isMobile ? '40px' : '50px',
                                opacity: 0.9
                            }}
                        />
                    </div>
                )}
            </div>
            )}

            {/* Overlay - Visible when sidebar is open (desktop and mobile) */}
            {isOpen && (
                <div
                    onClick={() => setIsOpen(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: window.innerWidth <= 768 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.3)',
                        zIndex: 999,
                        display: 'block'
                    }}
                />
            )}

            {/* Sidebar */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: isOpen ? 0 : '-280px',
                    height: '100vh',
                    width: '280px',
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
                    transition: 'left 0.3s ease',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto'
                }}
            >
                {/* Sidebar Header */}
                <div style={{
                    padding: '30px 20px 20px 20px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    marginTop: '20px'
                }}>
                    <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '700',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <FaBuilding size={20} />
                        Mi Portal
                    </div>
                    <div style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '13px',
                        wordBreak: 'break-word'
                    }}>
                        {userInfo?.email || 'Admin'}
                    </div>
                </div>

                {/* Menu Items */}
                <div style={{
                    flex: 1,
                    padding: '20px 0'
                }}>
                    {menuItems.map((item, index) => {
                        // Handle divider
                        if (item.type === 'divider') {
                            return (
                                <div 
                                    key={`divider-${index}`}
                                    style={{
                                        height: '1px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        margin: '15px 20px'
                                    }}
                                />
                            );
                        }

                        // Determine if this item is active
                        const isActive = activeView === item.id;

                        return (
                            <button
                                key={item.id}
                                onClick={() => handleNavigation(item.id)}
                                style={{
                                    width: '100%',
                                    padding: '15px 25px',
                                    background: isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                                    border: 'none',
                                    borderLeft: isActive ? '4px solid white' : '4px solid transparent',
                                    color: 'white',
                                    fontSize: '15px',
                                    fontWeight: isActive ? '600' : '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    textAlign: 'left'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = 'transparent';
                                    }
                                }}
                            >
                                <span style={{ fontSize: '18px' }}>{item.icon}</span>
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Sidebar Footer */}
                <div style={{
                    padding: '20px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <button
                        onClick={() => {
                            onLogout();
                            setIsOpen(false);
                        }}
                        style={{
                            width: '100%',
                            padding: '12px 20px',
                            background: 'rgba(255, 255, 255, 0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '10px',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                        }}
                    >
                        <FaSignOutAlt size={16} />
                        Cerrar sesión
                    </button>
                </div>
            </div>
        </>
    );
};

export default AdminSidebar;
